import { createContext, Dispatch, PropsWithChildren, SetStateAction, useEffect, useState } from 'react';
import { api } from '../services/api';

import { Theme } from '../types';

import { getItem, setItem } from '../utils/localStorage';

import { useCompanies } from '../hooks';

type Context = {
    token: string;
    loginError: string | null;
    theme: Theme;
    currentPage: string;
    organization: string;
    company: string;
    wabaIdSelected: string;
    companyName: string;
    setCurrentPage: Dispatch<SetStateAction<string>>;
    setTheme: Dispatch<SetStateAction<Theme>>;
    setToken: (token: string) => void;
    setLoginError: Dispatch<SetStateAction<string | null>>;
    setOrganization: Dispatch<SetStateAction<string>>;
    setCompany: Dispatch<SetStateAction<string>>;
    setCompanyName: Dispatch<SetStateAction<string>>;
    setWabaIdSelected: Dispatch<SetStateAction<string>>;
};

const initialContext: Context = {
    token: '',
    loginError: null,
    theme: { color: 'default', inverted: false },
    currentPage: '',
    organization: '',
    company: '',
    wabaIdSelected: '',
    companyName: '',
    setCurrentPage: () => ({}),
    setToken: () => ({}),
    setLoginError: () => ({}),
    setTheme: () => ({}),
    setOrganization: () => ({}),
    setCompany: () => ({}),
    setCompanyName: () => ({}),
    setWabaIdSelected: () => ({}),
};

const AppContext = createContext<Context>(initialContext);

const AppProvider = ({ children }: PropsWithChildren): JSX.Element => {
    const [token, setToken] = useState<string>(getItem<string>('token', ''));
    const [loginError, setLoginError] = useState<string | null>(null);
    const [theme, setTheme] = useState<Theme>({});
    const [currentPage, setCurrentPage] = useState('');
    const [organization, setOrganization] = useState('');
    const [company, setCompany] = useState('');
    const [companyName, setCompanyName] = useState('');
    const [wabaIdSelected, setWabaIdSelected] = useState('');

    const [companies] = useCompanies();

    const _setToken = (token: string) => {
        setItem('token', token);
        setToken(token);
        api.defaults.headers.common['authorization'] = token;
    };

    useEffect(() => {
        const currentCompany = companies.find(comp => comp.id == company);
        if (currentCompany?.company_business_messaging_waba_id) {
            setWabaIdSelected(currentCompany?.company_business_messaging_waba_id);
            setCompanyName(currentCompany.company_business_name);
        }
    }, [company, companies]);

    return (
        <AppContext.Provider
            value={{
                token,
                setToken: _setToken,
                loginError,
                setLoginError,
                theme,
                setTheme,
                currentPage,
                setCurrentPage,
                organization,
                setOrganization,
                company,
                setCompany,
                companyName,
                setCompanyName,
                wabaIdSelected,
                setWabaIdSelected,
            }}
        >
            {children}
        </AppContext.Provider>
    );
};

export { AppContext, AppProvider };
